import React, { useEffect, useState } from "react";
import { Alert, Button, Box } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { getTenantSettings, resendVerificationEmail } from "api/tenantApi";
import { unwrapObject } from "api/unwrap";

/**
 * Persistent banner that surfaces subscription state across every page:
 *   • trialing → "X days left in trial — upgrade now"
 *   • past_due → "Payment failed — update card"
 *   • canceled → "Subscription canceled — reactivate"
 *
 * Hidden on /billing/* pages so the user doesn't get nagged where they're
 * already managing it. Stripe lookup is best-effort server-side; we tolerate
 * missing fields silently.
 */
const DAY_MS = 86_400_000;

const fmtDaysLeft = (endMs) => {
  if (!endMs) return null;
  const ms = endMs - Date.now();
  if (ms <= 0) return "ended";
  const days = Math.ceil(ms / DAY_MS);
  return `${days} day${days === 1 ? "" : "s"}`;
};

export const TrialBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [s, setS] = useState(null);
  const [resending, setResending] = useState(false);
  const [resentAt, setResentAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getTenantSettings();
        const data = unwrapObject(res);
        if (!cancelled && data) setS(data);
      } catch {
        // Non-fatal — banner just won't render
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Don't nag on billing pages — that's where they go to fix it
  if (location.pathname.startsWith("/billing")) return null;
  if (!s) return null;

  // Email-verification nudge takes priority over subscription status nudges —
  // it's the more recoverable, more actionable signal for a new tenant.
  if (s.adminEmailVerified === false) {
    return (
      <Box sx={{ px: 2, pt: 1 }}>
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={resending || !!resentAt}
              onClick={async () => {
                setResending(true);
                try {
                  await resendVerificationEmail();
                  setResentAt(Date.now());
                } catch {
                  // Silent — banner stays; user can retry
                } finally {
                  setResending(false);
                }
              }}
              sx={{ fontWeight: 700 }}
            >
              {resentAt ? "Sent — check inbox" : resending ? "Sending…" : "Resend email"}
            </Button>
          }
          sx={{ borderRadius: 2 }}
        >
          Confirm your email address to unlock all features.
        </Alert>
      </Box>
    );
  }

  const status = s.subscriptionStatus;
  if (!status) return null;

  let severity = null;
  let message = null;
  let cta = null;

  if (status === "trialing" && s.trialEndMs) {
    const left = fmtDaysLeft(s.trialEndMs);
    severity = "info";
    message = `Trial: ${left} left on your ${s.plan || "starter"} plan.`;
    cta = "Upgrade now";
  } else if (status === "past_due") {
    severity = "warning";
    message = "Payment failed — update your card to keep service active.";
    cta = "Update payment";
  } else if (status === "canceled") {
    severity = "error";
    message = "Subscription canceled. Reactivate to keep using the platform.";
    cta = "Reactivate";
  } else if (status === "incomplete") {
    severity = "warning";
    message = "Subscription setup is incomplete — finish checkout to activate.";
    cta = "Finish checkout";
  }

  if (!severity) return null;

  return (
    <Box sx={{ px: 2, pt: 1 }}>
      <Alert
        severity={severity}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => navigate("/billing")}
            sx={{ fontWeight: 700 }}
          >
            {cta}
          </Button>
        }
        sx={{ borderRadius: 2 }}
      >
        {message}
      </Alert>
    </Box>
  );
};

export default TrialBanner;
